import { useMemo, useRef, useState } from "react";
import styles from "./FunnelsPage.module.scss";
import AnalyticsBars, { AnalyticsBarRow } from "../../../../common/components/analytics-component/bar-list/AnalyticsBars";
import AnalyticsCard from "../../../../common/components/analytics-component/card/AnalyticsCard";
import AnalyticsFilters from "../../../../common/components/analytics-component/filters/AnalyticsFilters";
import AnalyticsHeader from "../../../../common/components/analytics-component/header/AnalyticsHeader";
import AnalyticsMessage from "../../../../common/components/analytics-component/message/AnalyticsMessage";
import AnalyticsPageLayout from "../../../../common/components/analytics-component/page-layout/AnalyticsPageLayout";
import AnalyticsSectionHeader from "../../../../common/components/analytics-component/section-header/AnalyticsSectionHeader";
import AnalyticsStat from "../../../../common/components/analytics-component/stat/AnalyticsStat";
import AnalyticsState from "../../../../common/components/analytics-component/state/AnalyticsState";
import { useAnalyticsFilters } from "../../../../common/components/analytics-component/filters/useAnalyticsFilters";
import { BpsTabs } from "../../../../common/components/bps-admin/tabs/BpsTabs";
import EnergyManagementSection from "../energy-management/EnergyManagementSection";
import RetentionSection from "../retention/RetentionSection";
import {
	useGetCrossFlowFunnelQuery,
	useGetFlowErrorsQuery,
	useGetFlowFunnelQuery,
} from "../../../../service/business-analytics-service";
import { FlowFunnel } from "../../../../model/bps-admin/business-analytics";

// Which flow the vend funnel shows. The overview page carries its own copy of this
// code — the two have to stay in step.
const VEND_FLOW_CODE = "payment_vend";

const ONBOARDING_FLOW_CODE = "onboarding";

// Sorted here: the response carries an order field but no guarantee it arrives
// sorted. Every step reads neutral — there is no tone in the response.
const funnelRows = (funnel: FlowFunnel | undefined): AnalyticsBarRow[] =>
	[...(funnel?.steps ?? [])]
		.sort((first, second) => first.order - second.order)
		.map(step => ({
			label: step.name,
			percent: step.percentage,
			insideLabel: `${step.percentage}%`,
			trailing: step.reached.toLocaleString(),
		}));

// The tab strip is a table of contents, not a switch: every section stays rendered and
// the tab only moves the page to it.
const SECTIONS = [
	{ id: "funnels", label: "Funnels" },
	{ id: "retention", label: "Retention" },
	{ id: "energy-management", label: "Energy management" },
];

// Every flow's funnel, rather than the single vend funnel the overview carries, with
// retention and energy management under it.
const FunnelsPage = () => {
	const { rangeWithPlatform, controls } = useAnalyticsFilters();
	const [activeSection, setActiveSection] = useState(SECTIONS[0].id);

	// Kept in a map rather than one ref each: the scroll target is looked up by the id
	// the clicked tab carries.
	const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

	const goToSection = (id: string) => {
		setActiveSection(id);
		sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const {
		data: vendFunnel,
		isLoading: isLoadingFunnel,
		isFetching: isRefetchingFunnel,
		isError: isFunnelError,
		refetch: refetchFunnel,
	} = useGetFlowFunnelQuery({ code: VEND_FLOW_CODE, getErrors: true, ...rangeWithPlatform });

	const vendSteps = useMemo(() => funnelRows(vendFunnel), [vendFunnel]);

	// Signup through to a first vend: onboarding's stages, then payment_vend's completion.
	const {
		data: onboardingFunnel,
		isLoading: isLoadingOnboarding,
		isFetching: isRefetchingOnboarding,
		isError: isOnboardingError,
		refetch: refetchOnboarding,
	} = useGetCrossFlowFunnelQuery({
		code: ONBOARDING_FLOW_CODE,
		secondFlowCode: VEND_FLOW_CODE,
		...rangeWithPlatform,
	});

	// Same shape as funnelRows, but these steps count users rather than flows reached.
	const onboardingSteps = useMemo<AnalyticsBarRow[]>(
		() =>
			[...(onboardingFunnel?.steps ?? [])]
				.sort((first, second) => first.order - second.order)
				.map(step => ({
					label: step.name,
					percent: step.percentage,
					insideLabel: `${step.percentage}%`,
					trailing: step.users.toLocaleString(),
				})),
		[onboardingFunnel]
	);

	// The cross-funnel carries no errors, so onboarding's own come from a query of their own.
	const {
		data: onboardingErrors,
		isLoading: isLoadingOnboardingErrors,
		isFetching: isRefetchingOnboardingErrors,
		isError: isOnboardingErrorsError,
		refetch: refetchOnboardingErrors,
	} = useGetFlowErrorsQuery({ code: ONBOARDING_FLOW_CODE, ...rangeWithPlatform });

	return (
		<AnalyticsPageLayout title="Funnels, Retention & Energy Management" filters={<AnalyticsFilters {...controls} />}>
			<BpsTabs tabs={SECTIONS} activeTabId={activeSection} onTabChange={goToSection} />

			<section
				className={styles.section}
				ref={element => {
					sectionRefs.current[SECTIONS[0].id] = element;
				}}
			>
				<AnalyticsSectionHeader number="01" title={SECTIONS[0].label} />

				{/* Composed here rather than as components: the pieces are shared, these arrangements are not. */}
				<div className={styles.funnelRow}>
					<AnalyticsCard>
						<AnalyticsHeader
							title="Onboarding → activation"
							subtitle="Signup to first vend · activation = first_vend"
						/>
						<AnalyticsState
							loading={isLoadingOnboarding}
							error={isOnboardingError}
							onRetry={refetchOnboarding}
							retrying={isRefetchingOnboarding}
							empty={onboardingSteps.length === 0}
						>
							<AnalyticsBars rows={onboardingSteps} />
							{/* The mock's median time-to-first-vend has nothing behind it, so only the
							    biggest leak is named — and only when some step lost anyone. */}
							{onboardingFunnel && (
								<AnalyticsMessage
									tone="negative"
									title={`Only ${onboardingFunnel.conversionPercentage}% of signups reach a first vend`}
								>
									{onboardingFunnel.highestDropStepName &&
										`Biggest leak: ${onboardingFunnel.highestDropStepName}.`}
								</AnalyticsMessage>
							)}
						</AnalyticsState>
						{/* Never empty: a flow with no errors simply has no alerts under it. */}
						<AnalyticsState
							loading={isLoadingOnboardingErrors}
							error={isOnboardingErrorsError}
							onRetry={refetchOnboardingErrors}
							retrying={isRefetchingOnboardingErrors}
						>
							{onboardingErrors?.map(failure => (
								<AnalyticsMessage
									key={`${failure.stepCode}-${failure.reason}`}
									tone="negative"
									title={`${failure.stepName} · ${failure.instances.toLocaleString()} (${failure.percentage}%)`}
								>
									{failure.reason}
								</AnalyticsMessage>
							))}
						</AnalyticsState>
					</AnalyticsCard>

					<AnalyticsCard>
						<AnalyticsHeader
							title="Payment → vend"
							subtitle={vendFunnel ? `${vendFunnel.flowsStarted.toLocaleString()} flows` : undefined}
						/>
						<AnalyticsState
							loading={isLoadingFunnel}
							error={isFunnelError}
							onRetry={refetchFunnel}
							retrying={isRefetchingFunnel}
							empty={vendSteps.length === 0}
						>
							<AnalyticsBars rows={vendSteps} />
						</AnalyticsState>
						{/* Same query as the bars, but a state of its own: a flow with steps and no
						    failures is "no data" here and not above. The response carries no tone,
						    so every figure reads neutral. */}
						<AnalyticsHeader title="Failure breakdown" />
						<AnalyticsState
							loading={isLoadingFunnel}
							error={isFunnelError}
							onRetry={refetchFunnel}
							retrying={isRefetchingFunnel}
							empty={!vendFunnel?.topErrors?.length}
						>
							<div className={styles.failureGrid}>
								{vendFunnel?.topErrors.map(failure => (
									<AnalyticsStat
										key={`${failure.stepCode}-${failure.reason}`}
										label={failure.stepName}
										value={failure.instances.toLocaleString()}
										aside={`${failure.percentage}%`}
										note={failure.reason}
									/>
								))}
							</div>
						</AnalyticsState>
					</AnalyticsCard>
				</div>
			</section>

			<section
				className={styles.section}
				ref={element => {
					sectionRefs.current[SECTIONS[1].id] = element;
				}}
			>
				<AnalyticsSectionHeader number="02" title={SECTIONS[1].label} />
				<RetentionSection filters={rangeWithPlatform} />
			</section>

			<section
				className={styles.section}
				ref={element => {
					sectionRefs.current[SECTIONS[2].id] = element;
				}}
			>
				<AnalyticsSectionHeader number="03" title={SECTIONS[2].label} />
				<EnergyManagementSection filters={rangeWithPlatform} />
			</section>
		</AnalyticsPageLayout>
	);
};

export default FunnelsPage;
